import { RJSFSchema } from '@rjsf/utils';

import { filterFlashRoomRates } from '~/components/Offers/Detail/OfferBuilder/Content/OfferPackages/autofillPackage';
import {
  getPackagePersistId,
  getPackageRoomRateId,
  getPackageRoomTypeId,
  getRoomRateLabel,
} from '~/components/Offers/Detail/OfferBuilder/Content/OfferPackages/utils';

export { filterFlashRoomRates };

export const UNASSIGNED_ROOM_TYPE_ID = 'unassigned';

export type PendingLinks = Record<string, string>;

export interface DealOptionFilters {
  searchQuery: string;
  roomTypeFilter: Array<string>;
  roomRateFilter: Array<string>;
  statusFilter: string;
}

export function getSearchableDealOptionName(dealOption: App.AccommodationPackage) {
  return dealOption.deal_option_name || dealOption.name || getPackageRoomRateId(dealOption) || '';
}

export function buildRoomRateIndex(roomTypes: Array<App.RoomType>) {
  const byId = new Map<string, App.RoomRate>();
  roomTypes.forEach((roomType) => (roomType.room_rates ?? []).forEach((rate) => byId.set(rate.id, rate)));
  return byId;
}

export function filterDealOptions(
  dealOptions: Array<App.AccommodationPackage>,
  filters: DealOptionFilters,
  pendingLinks: PendingLinks,
  roomRateById: Map<string, App.RoomRate>,
) {
  const query = filters.searchQuery.trim().toLowerCase();

  return dealOptions.filter((dealOption) => {
    if (query && !getSearchableDealOptionName(dealOption).toLowerCase().includes(query)) {
      return false;
    }
    if (
      filters.roomTypeFilter.length &&
      !filters.roomTypeFilter.includes(getPackageRoomTypeId(dealOption) || UNASSIGNED_ROOM_TYPE_ID)
    ) {
      return false;
    }
    const packageId = getPackagePersistId(dealOption);
    const roomRateId = (packageId && pendingLinks[packageId]) || getPackageRoomRateId(dealOption) || '';
    const roomRate = roomRateById.get(roomRateId);
    const roomRateLabel = roomRate ? getRoomRateLabel(roomRate) || roomRate.id : '';
    if (filters.roomRateFilter.length && !filters.roomRateFilter.includes(roomRateLabel)) {
      return false;
    }
    if (filters.statusFilter && dealOption.status !== filters.statusFilter) {
      return false;
    }
    return true;
  });
}

export function groupDealOptionsByRoomType(dealOptions: Array<App.AccommodationPackage>) {
  const grouped = new Map<string, Array<App.AccommodationPackage>>();
  const sortedDealOptions = [...dealOptions].sort(
    (first, second) =>
      (first.number_of_nights ?? Number.POSITIVE_INFINITY) - (second.number_of_nights ?? Number.POSITIVE_INFINITY),
  );
  sortedDealOptions.forEach((dealOption) => {
    const roomTypeId = getPackageRoomTypeId(dealOption) || UNASSIGNED_ROOM_TYPE_ID;
    grouped.set(roomTypeId, [...(grouped.get(roomTypeId) ?? []), dealOption]);
  });
  return grouped;
}

export function getFilterableRoomTypes(dealOptions: Array<App.AccommodationPackage>, roomTypes: Array<App.RoomType>) {
  const dealOptionRoomTypeIds = new Set(dealOptions.map((dealOption) => getPackageRoomTypeId(dealOption)));
  return roomTypes.filter((roomType) => dealOptionRoomTypeIds.has(roomType.id));
}

// Dedupe by label: distinct room types can have differently-id'd rate plans with the same name.
export function getFilterableRoomRates(dealOptions: Array<App.AccommodationPackage>, roomTypes: Array<App.RoomType>) {
  const dealOptionRoomRateIds = new Set(dealOptions.map((dealOption) => getPackageRoomRateId(dealOption)));
  const seenByLabel = new Map<string, App.RoomRate>();
  roomTypes.forEach((roomType) =>
    (roomType.room_rates ?? []).forEach((rate) => {
      if (dealOptionRoomRateIds.has(rate.id)) {
        const label = getRoomRateLabel(rate) || rate.id;
        if (!seenByLabel.has(label)) {
          seenByLabel.set(label, rate);
        }
      }
    }),
  );
  return [...seenByLabel.values()];
}

export function getSaveResultMessage(savedCount: number, failedCount: number) {
  if (failedCount === 0) {
    return { message: `Saved ${savedCount} deal option${savedCount === 1 ? '' : 's'}`, variant: 'success' as const };
  }
  if (savedCount === 0) {
    return {
      message: `${failedCount} deal option${failedCount === 1 ? '' : 's'} failed to save`,
      variant: 'error' as const,
    };
  }
  return {
    message: `Saved ${savedCount} deal option${savedCount === 1 ? '' : 's'}, ${failedCount} failed to save`,
    variant: 'warning' as const,
  };
}

export const VISIBLE_SCHEMA_FIELDS = [
  'deal_option_name',
  'name',
  'campaign_code',
  'fk_room_type_id',
  'fk_room_rate_id',
  'status',
  'flexible_nights',
  'allow_buy_now_book_later',
  'allow_dates_request',
  'show_inclusions_validity_tag',
] as const;

const FALLBACK_PROPERTIES: Record<string, RJSFSchema> = {
  deal_option_name: { type: 'string', title: 'Deal option name' },
  name: { type: 'string', title: 'Name' },
  campaign_code: { type: 'string', title: 'Campaign code' },
  flexible_nights: { type: 'boolean', title: 'Enable flexible nights' },
  allow_buy_now_book_later: { type: 'boolean', title: 'Enable buy now choose later' },
  allow_dates_request: { type: 'boolean', title: 'Enable inventory requests' },
  show_inclusions_validity_tag: { type: 'boolean', title: 'Show inclusions validity tag' },
};

export function pickVisiblePackageSchema(schema: RJSFSchema): RJSFSchema {
  const sourceProperties = (schema.properties ?? {}) as Record<string, RJSFSchema>;
  const properties: Record<string, RJSFSchema> = {};
  VISIBLE_SCHEMA_FIELDS.forEach((field) => {
    const property = sourceProperties[field] ?? FALLBACK_PROPERTIES[field];
    if (property) {
      properties[field] = property;
    }
  });
  const required = Array.isArray(schema.required) ? schema.required.filter((field) => field in properties) : undefined;
  return { ...schema, properties, required };
}

export function overlayVisiblePackageFields(
  current: App.AccommodationPackage,
  next: Record<string, unknown>,
): App.AccommodationPackage {
  const overlay: Record<string, unknown> = {};
  VISIBLE_SCHEMA_FIELDS.forEach((field) => {
    if (field in next) {
      overlay[field] = next[field];
    }
  });
  return { ...current, ...overlay };
}

export function getDealOptionLabel(pkg: App.AccommodationPackage) {
  return pkg.deal_option_name || pkg.name || 'Unnamed deal option';
}

export function getPackageSaveErrorMessages(error: unknown): Array<string> {
  if (error && typeof error === 'object' && 'errors' in error && Array.isArray(error.errors)) {
    const messages = error.errors
      .map((err) => (typeof err === 'string' ? err : err?.message))
      .filter((message): message is string => Boolean(message));
    if (messages.length) {
      return messages;
    }
  }
  if (error instanceof Error && error.message) {
    return [error.message];
  }
  return ['Could not save this deal option'];
}

export function getAvailabilityRatesUrl(pkg: App.AccommodationPackage, offerId: string) {
  if (!offerId || !pkg.fk_room_type_id || !pkg.fk_room_rate_id) {
    return undefined;
  }
  const searchParams = new URLSearchParams({
    tab: 'room-types',
    roomTypeId: pkg.fk_room_type_id,
    roomRateId: pkg.fk_room_rate_id,
  });
  return `/offers/${offerId}/offer-builder/rates-and-inventory?${searchParams.toString()}`;
}

export function getRoomTypeContentUrl(pkg: App.AccommodationPackage, offerId: string) {
  if (!offerId || !pkg.fk_room_type_id) {
    return undefined;
  }
  const searchParams = new URLSearchParams({ tab: 'rooms', roomTypeId: pkg.fk_room_type_id });
  return `/offers/${offerId}/offer-builder/content?${searchParams.toString()}`;
}

export function getRatePlanUrl(pkg: App.AccommodationPackage, ratePlanId: string | undefined, offerId: string) {
  if (!offerId || !pkg.fk_room_type_id || !ratePlanId) {
    return undefined;
  }
  const searchParams = new URLSearchParams({
    tab: 'room-types',
    roomTypeId: pkg.fk_room_type_id,
    ratePlanId,
  });
  return `/offers/${offerId}/offer-builder/rates-and-inventory?${searchParams.toString()}`;
}

export function findRoomTypeByOrder(iterateIndex: number, roomTypes: Array<App.RoomType>) {
  for (let index = iterateIndex; index < roomTypes.length; index++) {
    const roomType = roomTypes[index];
    if (filterFlashRoomRates(roomType.room_rates ?? []).length) {
      return roomType;
    }
  }
  return undefined;
}
